
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../base/Button';
import logo from '../../assets/images/logo.png';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isPortalDropdownOpen, setIsPortalDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const portalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle scroll to add a shadow / background blur after scrolling a little
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container') && !target.closest('.portal-container')) {
        setActiveDropdown(null);
        setIsPortalDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    {
      name: 'Home',
      href: '/',
    },
    {
      name: 'About Us',
      href: '/about',
      dropdown: [
        { name: 'Overview', href: '/about' },
        { name: 'History', href: '/about/history' },
        { name: 'Message from the Principal', href: '/about/principal-message' },
        { name: 'Message from the Director', href: '/about/director-message' },
        { name: 'Advisory Board', href: '/about/advisory-board' },
        { name: 'Our Team', href: '/about/team' },
        { name: 'Vacancies', href: '/about/vacancies' },
      ],
    },
    {
      name: 'Admissions',
      href: '/admissions',
      dropdown: [
        { name: 'Overview', href: '/admissions' },
        { name: 'Admission Process', href: '/admissions/process' },
        { name: 'Entrance Exams', href: '/admissions/exams' },
        { name: 'Virtual Tour', href: '/admissions/virtual-tour' },
      ],
    },
    {
      name: 'Academics',
      href: '/academics',
      dropdown: [
        { name: 'Overview', href: '/academics' },
        { name: 'Early Years', href: '/academics/early-years' },
        { name: 'Primary', href: '/academics/primary' },
        { name: 'Secondary', href: '/academics/secondary' },
      ],
    },
    {
      name: 'Student Life',
      href: '/student-life',
      dropdown: [
        { name: 'Overview', href: '/student-life' },
        { name: 'Sports', href: '/student-life/sports' },
        { name: 'Leadership Programs', href: '/student-life/leadership' },
        // { name: 'Alumni', href: '/student-life/alumni' },
      ],
    },
    {
      name: 'Gallery',
      href: '/gallery',
      dropdown: [
        { name: 'Image Gallery', href: '/gallery' },
        { name: 'Video Gallery', href: '/gallery/video' },
      ],
    },


    {
      name: 'Contact',
      href: '/contact',
    },
  ];

  const portalItems = [
 { name: 'CBT Portal', href: 'https://cbt.schoolpetal.com/', icon: 'ri-computer-line' },
 { name: 'Admin Portal', href: 'https://portal.schoolpetal.com/', icon: 'ri-admin-line' },
    { name: 'Teachers Portal', href: 'https://portal.schoolpetal.com/', icon: 'ri-user-star-line' },
    { name: 'Counsellors Portal', href: 'https://portal.schoolpetal.com/', icon: 'ri-user-star-line' },
    { name: 'Parent Portal', href: 'https://portal.schoolpetal.com/parent-login', icon: 'ri-parent-line' },
    { name: 'Student Portal', href: 'https://portal.schoolpetal.com/student-portal', icon: 'ri-file-search-line' },
 
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleDropdownEnter = (itemName: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(itemName);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handlePortalEnter = () => {
    if (portalTimeoutRef.current) {
      clearTimeout(portalTimeoutRef.current);
    }
    setIsPortalDropdownOpen(true);
  };

  const handlePortalLeave = () => {
    portalTimeoutRef.current = setTimeout(() => {
      setIsPortalDropdownOpen(false);
    }, 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 lg:space-x-3">
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-800 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg lg:text-xl"><img src={logo} alt="" /></span>
            </div>
            <div>
              <h1
                className="text-xl lg:text-2xl font-bold text-green-800"
                style={{ fontFamily: 'Pacifico, serif' }}
              >
                My Rehoboth Schools
              </h1>
              <p className="text-xs text-gray-600 hidden sm:block">...with God all things are possible</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative dropdown-container"
                onMouseEnter={() => item.dropdown && handleDropdownEnter(item.name)}
                onMouseLeave={() => item.dropdown && handleDropdownLeave()}
              >
                <Link
                  to={item.href}
                  className={`text-gray-700 hover:text-green-800 font-medium transition-colors duration-200 text-sm xl:text-base flex items-center ${
                    isActive(item.href) ? 'text-green-800' : ''
                  }`}
                >
                  {item.name}
                  {item.dropdown && <i className="ri-arrow-down-s-line ml-1 text-sm"></i>}
                </Link>

                {/* Dropdown Menu */}
                {item.dropdown && activeDropdown === item.name && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                    onMouseEnter={() => handleDropdownEnter(item.name)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className="block px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors duration-200 text-sm"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Portal button & mobile menu */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Portal button for desktop */}
            <div 
              className="relative hidden sm:block portal-container"
              onMouseEnter={handlePortalEnter}
              onMouseLeave={handlePortalLeave}
            >
              <Button
                variant="primary"
                size="sm"
                className="text-sm flex items-center"
                onClick={() => setIsPortalDropdownOpen(!isPortalDropdownOpen)}
              >
                <i className="ri-user-line mr-2"></i>
                School Portal
                <i className="ri-arrow-down-s-line ml-2"></i>
              </Button>

              {/* Portal Dropdown */}
              {isPortalDropdownOpen && (
                <div 
                  className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                  onMouseEnter={handlePortalEnter}
                  onMouseLeave={handlePortalLeave}
                >
                  {portalItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                     
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-900 transition-colors duration-200 text-sm"
                      onClick={() => setIsPortalDropdownOpen(false)}
                    >
                      <i className={`${item.icon} mr-3`}></i>
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 hover:text-green-800 hover:bg-gray-100"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 bg-white">
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {navigation.map((item) => (
                <div key={item.name} className="space-y-1">
                  <Link
                    to={item.href}
                    className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-800 rounded-lg transition-colors duration-200"
                    onClick={() => {
                      if (!item.dropdown) {
                        setIsMenuOpen(false);
                      }
                    }}
                  >
                    <span>{item.name}</span>
                    {item.dropdown && (
                      <i className="ri-arrow-down-s-line text-sm"></i>
                    )}
                  </Link>
                  
                  {/* Mobile sub-items */}
                  {item.dropdown && (
                    <div className="ml-4 space-y-1 border-l-2 border-green-100 pl-4">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2 text-sm text-gray-600 hover:bg-green-50 hover:text-green-800 rounded-lg transition-colors duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile Portal menu */}
              <div className="px-4 pt-4 border-t border-gray-200 mt-4">
                <p className="text-sm font-medium text-gray-900 mb-3 px-4">School Portal</p>
                <div className="space-y-1">
                  {portalItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-sm text-gray-600 hover:bg-green-50 hover:text-green-800 rounded-lg transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className={`${item.icon} mr-3`}></i>
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
